import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BedarfStatus } from '../types/bedarf';

export type ViewMode = 'table' | 'kanban';

export interface BedarfeFilters {
  search: string;
  status: BedarfStatus | '';
  schoolCity: string;
  assignedUserId: string;
  view: ViewMode;
}

export function useBedarfeFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL
  const filters = useMemo<BedarfeFilters>(
    () => ({
      search: searchParams.get('search') || '',
      status: (searchParams.get('status') as BedarfStatus) || '',
      schoolCity: searchParams.get('schoolCity') || '',
      assignedUserId: searchParams.get('assignedUserId') || '',
      view: searchParams.get('view') === 'table' ? 'table' : 'kanban',
    }),
    [searchParams]
  );

  const setFilter = useCallback(
    <K extends keyof BedarfeFilters>(key: K, value: BedarfeFilters[K]) => {
      setSearchParams(
        (prev) => {
          if (value) {
            prev.set(key, value as string);
          } else {
            prev.delete(key);
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.delete('search');
        prev.delete('status');
        prev.delete('schoolCity');
        prev.delete('assignedUserId');
        // Keep view mode
        return prev;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(
    () => Boolean(filters.search || filters.status || filters.schoolCity || filters.assignedUserId),
    [filters]
  );

  return {
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  };
}
